import { Classes, Dialog, InputGroup, Spinner, TextArea } from "@blueprintjs/core"
import { Field, FieldProps } from "formik"
import { observer } from "mobx-react"
import { CollapseExpandAll } from "modules/common/components/table/CollapseExpandAll"
import { OrderExternalParams, OrderSummaryType, sortOrderObjects } from "modules/orders-manage/models/order"
import { SectionCollapserType } from "modules/orders-manage/models/orders-store"
import { Constants } from "modules/root/models/constants"
import React from "react"
import { CategoryBlock, signatureTypeList } from "./CategoryBlock"
import styles from "./ContentTask.module.scss"

import { OutsourcerDictionaryType } from 'modules/agents/outsourcers/models/outsourcer-dictionary'
import { FileLink } from "modules/common/components/files/FileLink"
import { Buttons } from "modules/common/components/form"
import { DatePicker } from "modules/common/components/form/DatePicker"
import { StandardFormInput } from "modules/common/components/form/StandardFormInput"
import { PlanrButton } from "modules/common/components/planr/button/Button"
import { PlanRDndUploader } from "modules/common/components/planr/dndUploader/FileDndUploader"
import { GeneralIcon } from "modules/common/components/planr/icon/Generalcon"
import { DATE_TIME_FORMAT } from "modules/common/constants"
import { DefaultSelectedOption, filterItemPredicate, renderSingleOption, SimpleSelect } from "modules/common/services/form/select"
import { formatDate } from "modules/common/services/formatting/date"
import { ProjectPortfolioDictionaryType } from 'modules/dictionaries/project-portfolios/models/project-portfolio-dictionary'
import { PendingSignatureItemType, SignatureDtoType } from 'modules/main/models/order-content-task-signatures'
import { OrderContentTasksDictionaryType } from "modules/orders-manage/models/order-content-tasks"
import { EmployerDictionaryType } from "modules/spending/employee/models/employee-dictionary"
import moment from "moment"
import { RouteComponentProps } from "modules/common/components/routing/withRouter";
import { withRouter } from "modules/common/components/routing/withRouter";
import { Schema } from "yup"
import { ScrollToElement } from "../spending/helpers"
import { DownloadButton, RowTask } from "./RowTask"


// Функция для определения цвета на основе даты окончания ЭЦП
export const getEcpColor = (signDateTo: string | null | undefined): string | undefined => {
  if (!signDateTo) return undefined

  const expiryDate = moment(signDateTo, 'DD.MM.YYYY')
  const currentDate = moment().startOf('day')

  if (expiryDate.isBefore(currentDate)) {
    return 'rgba(227, 24, 24, 0.1)' // просрочена
  } else {
    return '#1dd2781a' // действует
  }
}

// Интерфейсы для состояний
interface TTaskFile {
  id: string
  name: string
}

interface TTaskFilePath {
  id: string
  name: string
  path: string
}

interface TResultWorker {
  id: string
  name: string
  type: TWorkType
  signed: boolean
}

type TWorkType = 'worked' | 'checked' | 'control' | 'legal' | 'gip'

interface ContentState {
  downloading: boolean
  selectedBlockGuid: string | null
  // Состояния для диалогов
  showDelete: boolean
  showUploadFiles: boolean
  showAdd: boolean
  showSheet: boolean
  showUnits: boolean
  showResetWarning: boolean
  workGuid: string
  taskId: string
  description: string
  workFiles: TTaskFile[]
  taskFiless: TTaskFile[]
  taskFiles: TTaskFilePath[]
  selectedUnits: string[]
  saveUnits: string[]
  employersWorked: TResultWorker[]
  employersLegal: TResultWorker[]
  isUploading: boolean
  stopDate: Date | null
  implementer: SelectItem
  selectedType: { id: string; label: string }
  legal: boolean
  currentFileId: string
  implementerSelectKey: number
  isEditeMode: boolean

  showNoEcpWarning: boolean
  noEcpEmployees: Array<{ name: string, type: string, personType: string }>
  expiredEcpEmployees: Array<{ name: string, type: string, personType: string, expiryDate: string }>

  showDownloadDialog: boolean
  selectedCategoriesForDownload: string[]
  availableCategories: Array<{
    guid: string,
    name: string,
    category: string, description: string,
    sourceFilesCount: number,
    resultFilesCount: number,
    totalFilesCount: number,
    orderIndex: number
  }>
  selectAllInCategory: { [key: string]: boolean }
  downloadSourceFiles: boolean  // Только этот переключатель
}

class ContentBase extends React.Component<ContentProps & RouteComponentProps, ContentState> {
  mounted = true;
  private me = React.createRef<HTMLDivElement>();
  recordPointer = "";
  private fieldProps: FieldProps | null = null;

  constructor(props: ContentProps & RouteComponentProps) {
    super(props)

    const external = OrderExternalParams(this.props.location.hash)
    this.recordPointer = external.rowId
    this.state = {
      downloading: false,
      selectedBlockGuid: null,
      // Инициализация состояний диалогов
      showDelete: false,
      showUploadFiles: false,
      showAdd: false,
      showSheet: false,
      showUnits: false,
      showResetWarning: false,
      workGuid: "",
      taskId: "",
      description: "",
      workFiles: [],
      taskFiless: [],
      taskFiles: [],
      selectedUnits: [],
      saveUnits: [],
      employersWorked: [],
      employersLegal: [],
      isUploading: false,
      stopDate: null,
      implementer: { id: "", label: "" },
      selectedType: { id: "", label: "" },
      legal: false,
      currentFileId: "",
      implementerSelectKey: 0,
      isEditeMode: false,
      showNoEcpWarning: false,
      noEcpEmployees: [],
      expiredEcpEmployees: [],

      showDownloadDialog: false,
      selectedCategoriesForDownload: [],
      availableCategories: [],
      selectAllInCategory: {},
      downloadSourceFiles: true,
    }
  }

  componentDidMount() {
    this.mounted = true
    ScrollToElement(this.me, this.recordPointer || "", 1000)
    if (this.recordPointer !== "" && this.me) {
      this.collapseAll(false)
    }
  }

  componentWillUnmount() {
    this.mounted = false
    this.setState({ downloading: false })
  }

  prepareDownloadCategories = () => {
    const { store, order } = this.props
    const categories: Array<{
      guid: string,
      name: string,
      description: string,
      category: string,
      sourceFilesCount: number,
      resultFilesCount: number,
      totalFilesCount: number,
      orderIndex: number // Добавляем индекс для сортировки
    }> = []

    // Создаем массив всех unit в том порядке, как они отображаются на странице
    const allUnitsInOrder: Array<{ guid: string, name: string, description: string, category: string, orderIndex: number }> = []

    order.objects.forEach((object) => {
      object.content.forEach((unit, index) => {
        allUnitsInOrder.push({
          guid: unit.guid,
          name: unit.name,
          description: unit.description,
          category: unit.workTypeCategory || 'Без категории',
          orderIndex: allUnitsInOrder.length // Сохраняем порядковый номер
        })
      })
    })

    // Создаем map для быстрого поиска индекса по guid
    const orderIndexMap = new Map(
      allUnitsInOrder.map((unit) => [unit.guid, unit.orderIndex])
    )

    // Проходим по всем task в store
    for (let item of store.contentTasksList) {
      // Функция для получения уникальных файлов (только последние версии)
      const getLatestVersions = (files: Array<{ id: string; name: string; version: number }> = []) => {
        const filesByName: { [key: string]: Array<{ id: string; name: string; version: number }> } = {}

        files.forEach((file) => {
          if (!filesByName[file.name]) {
            filesByName[file.name] = []
          }
          filesByName[file.name].push(file)
        })

        return Object.values(filesByName).map((fileGroup) => {
          return fileGroup.reduce((max, current) => {
            return current.version > max.version ? current : max
          })
        })
      }

      const latestSourceFiles = getLatestVersions(item.sourceFiles)
      const latestResultFiles = getLatestVersions(item.resultFiles)

      const hasFiles = latestSourceFiles.length > 0 || latestResultFiles.length > 0

      if (hasFiles) {
        const unit = allUnitsInOrder.find((u) => u.guid === item.guid)

        if (unit) {
          categories.push({
            guid: item.guid,
            name: unit.name,
            description: unit.description,
            category: unit.category,
            sourceFilesCount: latestSourceFiles.length,
            resultFilesCount: latestResultFiles.length,
            totalFilesCount: latestSourceFiles.length + latestResultFiles.length,
            orderIndex: orderIndexMap.get(item.guid) || 0
          })
        }
      }
    }

    // Сортируем по порядковому индексу, чтобы сохранить порядок как на странице
    const sortedCategories = categories.sort((a, b) => a.orderIndex - b.orderIndex)

    // Создаем объект для отслеживания выбора "все в категории"
    const selectAllInCategory: { [key: string]: boolean } = {}
    const categoriesSet = new Set(sortedCategories.map((c) => c.category))
    categoriesSet.forEach((cat) => {
      selectAllInCategory[cat] = true
    })

    this.setState({
      availableCategories: sortedCategories,
      selectedCategoriesForDownload: sortedCategories.map((c) => c.guid),
      selectAllInCategory
    })
  }

  openDownloadDialog = () => {
    this.prepareDownloadCategories()
    this.setState({ showDownloadDialog: true })
  }

  toggleCategorySelection = (guid: string) => {
    this.setState((prevState) => {
      const isSelected = prevState.selectedCategoriesForDownload.includes(guid)
      let newSelection = isSelected
        ? prevState.selectedCategoriesForDownload.filter((g) => g !== guid)
        : [...prevState.selectedCategoriesForDownload, guid]

      // Обновляем состояние "выбрать все" для категории
      const updatedCategoryState = { ...prevState.selectAllInCategory }

      // Для каждой категории проверяем, все ли элементы выбраны
      const categoriesSet = new Set(prevState.availableCategories.map((c) => c.category))
      categoriesSet.forEach((cat) => {
        const itemsInCategory = prevState.availableCategories.filter((c) => c.category === cat)
        const allSelectedInCategory = itemsInCategory.every((item) =>
          newSelection.includes(item.guid)
        )
        updatedCategoryState[cat] = allSelectedInCategory
      })

      return {
        selectedCategoriesForDownload: newSelection,
        selectAllInCategory: updatedCategoryState
      }
    })
  }

  toggleAllInCategory = (category: string) => {
    this.setState((prevState) => {
      const itemsInCategory = prevState.availableCategories.filter((c) => c.category === category)
      const itemsGuids = itemsInCategory.map((c) => c.guid)

      const newAllSelected = !prevState.selectAllInCategory[category]

      let newSelection = [...prevState.selectedCategoriesForDownload]

      if (newAllSelected) {
        // Добавляем все элементы категории
        itemsGuids.forEach((guid) => {
          if (!newSelection.includes(guid)) {
            newSelection.push(guid)
          }
        })
      } else {
        // Удаляем все элементы категории
        newSelection = newSelection.filter((guid) => !itemsGuids.includes(guid))
      }

      return {
        selectedCategoriesForDownload: newSelection,
        selectAllInCategory: {
          ...prevState.selectAllInCategory,
          [category]: newAllSelected
        }
      }
    })
  }

  selectAllCategories = () => {
    this.setState({
      selectedCategoriesForDownload: this.state.availableCategories.map((c) => c.guid),
      selectAllInCategory: Object.keys(this.state.selectAllInCategory).reduce((acc, cat) => {
        acc[cat] = true
        return acc
      }, {} as { [key: string]: boolean })
    })
  }

  deselectAllCategories = () => {
    this.setState({
      selectedCategoriesForDownload: [],
      selectAllInCategory: Object.keys(this.state.selectAllInCategory).reduce((acc, cat) => {
        acc[cat] = false
        return acc
      }, {} as { [key: string]: boolean })
    })
  }

  toggleDownloadSourceFiles = () => {
    this.setState((prevState) => ({
      downloadSourceFiles: !prevState.downloadSourceFiles
    }))
  }

  downloadSelectedCategories = async () => {
    if (this.state.selectedCategoriesForDownload.length === 0) {
      this.props.store.notify.warning('Не выбрано ни одной категории для скачивания')
      return
    }

    const arrToDownload = this.state.selectedCategoriesForDownload.map((guid) => ({
      contentGuid: guid
    }))

    try {
      this.setState({ downloading: true, showDownloadDialog: false })
      // Передаем параметр downloadSourceFiles в функцию store
      await this.props.store.downloadFiles(
        this.props.order.id,
        arrToDownload,
        this.props.order.name,
        this.state.downloadSourceFiles // true - скачивать исходные файлы, false - только результаты
      )
    } catch (error) {
      console.info('Ошибка при скачивании:', error)
    } finally {
      if (this.mounted) {
        this.setState({ downloading: false })
      }
    }
  }


  // Методы для управления диалогами
  toggleDeleteShow = () => {
    this.setState({ showDelete: !this.state.showDelete })
  };

  toggleShowUploadFiles = () => {
    this.setState({ showUploadFiles: !this.state.showUploadFiles, isUploading: false })
  };

  toggleShowDevelopers = () => {
    this.setState({
      showSheet: !this.state.showSheet,
      employersWorked: [],
      employersLegal: [],
      implementer: { id: "", label: "" },
      selectedType: { id: "", label: "" },
      legal: false
    })
  };

  showUploadFilesDialog = (guid: string) => {
    this.setState({ workGuid: guid, workFiles: [], taskFiless: [] })
    this.toggleShowUploadFiles()
  };

  showDeleteTask = (guid: string, id: string) => {
    this.setState({
      showDelete: true,
      taskId: id,
      workGuid: guid
    })
  };

  showAddTask = (guid: string) => {
    this.setState({
      showAdd: true,
      workGuid: guid,
      taskId: "",
      description: "",
      taskFiles: [],
      saveUnits: [guid],
      isEditeMode: false,
      stopDate: null,
      implementer: { id: "", label: "" },
    })
  };

  showEditTask = (
    description: string,
    stopDate: string | null,
    taskId: string,
    taskFiles: TTaskFilePath[], // Изменено с any на TTaskFilePath[]
    guid: string,
    employerId: string | null
  ) => {
    const implement = this.props.employee.employee.find((emp) => emp.id === employerId)
    this.setState({
      showAdd: true,
      workGuid: guid,
      taskId: taskId,
      description,
      taskFiles: [...taskFiles],
      isEditeMode: true,
      stopDate: stopDate ? moment(stopDate, DATE_TIME_FORMAT).toDate() : null,
      saveUnits: [guid],
      implementer: implement ? { id: implement.id, label: implement.name } : { id: "", label: "" },
    })
  };

  showDevelopersDialog = (guid: string, fileId: string) => {
    const contentTask = this.props.store.contentTasksList.find(
      (item) => item.guid === guid
    )

    if (contentTask) {
      const file = contentTask.resultFiles.find((f) => f.id === fileId)
      if (file) {
        this.setState({
          showSheet: true,
          workGuid: guid,
          currentFileId: fileId,
          employersWorked: file.signatures?.filter((f) => f.signerType !== 'legal').map((s) => ({
            id: s.employerId,
            name: s.employerName,
            type: s.signerType as TWorkType,
            signed: s.signed
          })) || [],
          employersLegal: file.signatures?.filter((f) => f.signerType === 'legal').map((s) => ({
            id: s.employerId,
            name: s.employerName,
            type: s.signerType as TWorkType,
            signed: s.signed
          })) || [],
          legal: (file.signatures?.filter((f) => f.signerType === 'legal').length || 0) > 0
        })
      }
    }
  };

  // Методы для работы с файлами
  workFilesUpload = async (file: File) => {
    const result = await this.props.store.uploadFile(file)
    if (result) {
      const newFile = { id: result?.fileId, name: result?.fileName }
      this.setState({ workFiles: [...this.state.workFiles, newFile] })
    }
  };

  taskFilesUpload = async (file: File) => {
    const result = await this.props.store.uploadFile(file)
    if (result) {
      const newFile = { id: result?.fileId, name: result?.fileName }
      this.setState({ taskFiless: [...this.state.taskFiless, newFile] })
    }
  };

  taskFilesLoad = async (file: any) => {
    const result = await this.props.store.uploadFile(file)
    if (result) {
      const files = [...this.state.taskFiles]
      files.push({ id: result?.fileId, name: result?.fileName, path: "" })
      this.setState({ taskFiles: files })
    }
  };

  removeWorkFilesFromState = (fileId: string) => {
    this.setState({ workFiles: this.state.workFiles.filter((item) => item.id !== fileId) })
  };

  removeTaskFilesFromState = (fileId: string) => {
    this.setState({ taskFiless: this.state.taskFiless.filter((item) => item.id !== fileId) })
  };

  resetFiles = (id: string) => {
    const files = [...this.state.taskFiles]
    const index = files.findIndex((f) => f.id === id)
    if (index > -1) {
      const newFiles = [...files.slice(0, index), ...files.slice(index + 1)]
      this.setState({ taskFiles: newFiles })
    }
  };

  // Методы для сохранения
  saveUploadedWorkFiles = async () => {
    if (this.state.isUploading) return
    this.setState({ isUploading: true })
    const result = await this.props.store.saveWorkFiles(
      this.props.order.id,
      this.state.workGuid,
      this.state.workFiles,
      this.state.taskFiless
    )
    if (result) {
      this.toggleShowUploadFiles()
    } else {
      this.setState({ isUploading: false })
    }
  };

  saveTask = async () => {
    const { taskFiles, description, saveUnits, implementer, stopDate, taskId } = this.state
    const employer = this.props.employee.employee.find((emp) => emp.id === implementer.id)

    const result = await this.props.store.addTaskTemp(
      taskId,
      saveUnits,
      description,
      formatDate(stopDate),
      taskFiles,
      this.props.user,
      implementer,
      employer
    )

    if (result && this.fieldProps) {
      // Используем сохраненный fieldProps
      this.fieldProps.form.setFieldValue(this.fieldProps.field.name, this.props.store.contentTasksList)
      this.fieldProps.form.setFieldTouched(this.fieldProps.field.name, true)

      this.closeAddDialog()
    }
  };

  deleteTask = (id: string) => {
    this.props.store.deleteTaskTemp(id)
  };

  saveDevelopers = async () => {
    const contentTask = this.props.store.contentTasksList.find(
      (item) => item.guid === this.state.workGuid
    )
    if (!contentTask) return

    const resultFile = contentTask.resultFiles?.find(
      (f) => f.id === this.state.currentFileId
    )
    if (!resultFile) return

    const existingSigners = resultFile.signatures || []
    const currentSignerIds = new Set([
      ...this.state.employersWorked.map((e) => e.id),
      ...this.state.employersLegal.map((e) => e.id)
    ])

    const removedSigners = existingSigners.filter(
      (signer) => !currentSignerIds.has(signer.employerId)
    )

    const hadSignatures = removedSigners.some((signer) => signer.signed)

    if (hadSignatures) {
      this.setState({ showResetWarning: true })
      return
    }

    // Проверка на наличие и срок действия ЭЦП у выбранных сотрудников и аутсорсеров
    const employeesWithoutEcp = []
    const employeesWithExpiredEcp = []
    const currentDate = moment().startOf('day')

    // Проверяем всех выбранных сотрудников (работающих)
    for (const emp of this.state.employersWorked) {
      // Проверяем в списке сотрудников
      const employeeData = this.props.employee.employee.find((e) => e.id === emp.id)
      // Проверяем в списке аутсорсеров
      const outsourcerData = this.props.agents.outsourcers.find((a) => a.id === emp.id)

      // Определяем тип и данные
      const personData = employeeData || outsourcerData
      const personType = employeeData ? 'сотрудник' : (outsourcerData ? 'аутсорсер' : 'неизвестно')

      if (!personData?.signDateTo) {
        employeesWithoutEcp.push({
          name: emp.name,
          type: this.showType(emp.type),
          personType: personType
        })
      } else {
        // Проверяем, не истек ли срок действия ЭЦП
        const expiryDate = moment(personData.signDateTo, 'DD.MM.YYYY')
        if (expiryDate.isBefore(currentDate)) {
          employeesWithExpiredEcp.push({
            name: emp.name,
            type: this.showType(emp.type),
            personType: personType,
            expiryDate: personData.signDateTo
          })
        }
      }
    }

    // Проверяем юридические лица
    for (const emp of this.state.employersLegal) {
      const employeeData = this.props.employee.employee.find((e) => e.id === emp.id)

      if (!employeeData?.signDateTo) {
        employeesWithoutEcp.push({
          name: emp.name,
          type: 'Юр. лицо',
          personType: 'сотрудник'
        })
      } else {
        const expiryDate = moment(employeeData.signDateTo, 'DD.MM.YYYY')
        if (expiryDate.isBefore(currentDate)) {
          employeesWithExpiredEcp.push({
            name: emp.name,
            type: 'Юр. лицо',
            personType: 'сотрудник',
            expiryDate: employeeData.signDateTo
          })
        }
      }
    }

    if (employeesWithoutEcp.length > 0 || employeesWithExpiredEcp.length > 0) {
      this.setState({
        noEcpEmployees: employeesWithoutEcp,
        expiredEcpEmployees: employeesWithExpiredEcp,
        showNoEcpWarning: true
      })
      return
    }

    await this.performSaveDevelopers()
  }

  confirmSaveDevelopers = async () => {
    this.setState({ showResetWarning: false })
    await this.performSaveDevelopers()
  };

  performSaveDevelopers = async () => {

    const fileName = this.props.store.contentTasksList
      .flatMap(((item) => item.files))
      .find((file) => file.id === this.state.currentFileId)?.name

    const result = await this.props.store.saveDevelopers(
      this.props.order.id,
      this.state.workGuid,
      this.state.currentFileId,
      this.state.employersWorked,
      this.state.employersLegal,
      this.state.legal, this.props.order.fullInventoryNumber + ' ' + this.props.order.name,
      fileName || "",
    )

    if (result) {
      this.toggleShowDevelopers()
    }
  };

  // Вспомогательные методы
  closeAddDialog = () => {
    this.setState({ showAdd: false })
  };

  showSelectedUnits = () => {
    this.setState({ showUnits: true, selectedUnits: [...this.state.saveUnits] })
  };

  setSelectedUnits = (guid: string) => {
    let newUnits = [...this.state.selectedUnits]
    const index = newUnits.findIndex((unit) => unit === guid)
    let newArr: string[] = newUnits
    if (index > -1) {
      newArr = [...newUnits.slice(0, index), ...newUnits.slice(index + 1)]
    } else {
      newArr.push(guid)
    }
    this.setState({ selectedUnits: newArr })
  };

  setSaveUnits = (guid: string) => {
    let newUnits = [...this.state.saveUnits]
    const index = newUnits.findIndex((unit) => unit === guid)
    let newArr: string[] = newUnits
    if (index > -1) {
      newArr = [...newUnits.slice(0, index), ...newUnits.slice(index + 1)]
    } else {
      newArr.push(guid)
    }
    this.setState({ saveUnits: newArr })
  };

  saveSelectedUnits = () => {
    this.setState({ saveUnits: this.state.selectedUnits, selectedUnits: [], showUnits: false })
  };

  cancelSelectedUnits = () => {
    this.setState({ selectedUnits: [], showUnits: false })
  };

  addNewDeveloper = () => {
    const { implementer, selectedType } = this.state
    if (implementer.id === '' || selectedType.id === '') return

    let cleanName = implementer.label
    if (cleanName.startsWith('(сотр) ')) {
      cleanName = cleanName.substring(7)
    }
    if (cleanName.startsWith('(аутс) ')) {
      cleanName = cleanName.substring(7)
    }

    let newItem = {
      id: this.state.implementer.id,
      name: cleanName,
      type: (selectedType.id as any),
      signed: false
    }

    this.setState({
      employersWorked: [...this.state.employersWorked, newItem],
      implementer: { id: "", label: "" },
      selectedType: { id: "", label: "" },
      implementerSelectKey: this.state.implementerSelectKey + 1
    })
  };

  addNewLegal = (employer: any) => {
    if (employer.id === '') return
    const newItem = {
      id: employer.id,
      name: employer.label,
      type: 'legal' as any,
      signed: false
    }
    this.setState({ employersLegal: [...this.state.employersLegal, newItem] })
  };

  removeDeveloper = (indexItem: number, type: string) => {
    if (type !== 'legal') {
      this.setState({ employersWorked: this.state.employersWorked.filter((item, index) => index !== indexItem) })
    }
    if (type === 'legal') {
      this.setState({ employersLegal: this.state.employersLegal.filter((item, index) => index !== indexItem) })
    }
  };

  showType = (type: TWorkType) => {
    const found = signatureTypeList.find((item) => item.id === type)
    return found?.label || type
  };

  // Методы для работы с задачами
  returnTask = (id: string) => {
    this.props.store.returnTaskTemp(id)
  };

  addAccept = async (guid: string, value: boolean, id: string) => {
    await this.props.store.saveAccepted(this.props.order.id, guid, value, id)
  };

  addComplete = async (guid: string, value: boolean, id: string) => {
    await this.props.store.saveCompleted(this.props.order.id, guid, value, id)
  };

  addConfirm = async (guid: string, value: boolean, id: string) => {
    await this.props.store.saveConfirmed(this.props.order.id, guid, value, id)
  };

  addCancell = async (guid: string, value: boolean, id: string) => {
    await this.props.store.saveCancelled(this.props.order.id, guid, value, id)
  };

  downloadFiles = async (contentGuid: string) => {
    await this.props.store.downloadFiles(this.props.order.id, [{ contentGuid }], this.props.order.name)
  };

  downloadVersion = async (contentGuid: string, fileId: string, version: number) => {
    await this.props.store.downloadVersion(this.props.order.id, contentGuid, fileId, version)
  };

  deleteVersion = (contentGuid: string, fileId: string, version: number) => {
    this.props.store.removeVersion(this.props.order.id, contentGuid, fileId, version)
  };

  handleSubmitSignature = async (
    signature: PendingSignatureItemType,
    thumbprint: string,
    signatureData: string,
    rowVersion: number,
    signerId?: string | null
  ) => {
    return await this.props.store.submitSignature(signature, signatureData, this.props.userId, rowVersion, signerId)
  };

  handleAttachSignature = async (signature: PendingSignatureItemType, signatureData: string, signerId?: string | null) => {
    return await this.props.store.attachSignature(signature, signatureData, signerId)
  }

  handleClearSignatures = async (signatureId: string, fileId: string) => {
    await this.props.store.clearSignatures(signatureId, fileId)
  }

  handleGetSignature = async (id: string): Promise<SignatureDtoType> => {
    const res = await this.props.store.getSignature(id)
    if (!res) {
      throw new Error('Не удалось получить данные подписи')
    }
    return res
  };

  handleNotifyError = (error: string) => {
    this.props.store.notify.error(error)
  };

  handleNotifySuccess = (success: string) => {
    this.props.store.notify.success(success)
  };

  collapseAll = (state: boolean) => {
    this.props.collapser.setAll(state)
    this.props.collapserContent.setAll(state)
  };

  saveFilePath = async (contentGuid: string, fileId: string, path: string) => {
    await this.props.store.saveFilePath(
      this.props.order.id,
      contentGuid,
      fileId,
      path
    )
  }

  render() {
    const {
      name,
      order,
      readOnly,
      collapser,
      collapserContent,
      store,
      baseUrl,
      user,
      clientCanDownloadField,
      schema,
      employee,
      agents,
      portfolios
    } = this.props

    const {
      selectedBlockGuid,
      showDelete,
      showUploadFiles,
      showAdd,
      showSheet,
      showUnits,
      showResetWarning,
      workGuid,
      taskId,
      description,
      workFiles,
      taskFiless,
      taskFiles,
      selectedUnits,
      saveUnits,
      employersWorked,
      employersLegal,
      isUploading,
      stopDate,
      implementer,
      selectedType,
      legal,
      // currentFileId,
      implementerSelectKey,
      isEditeMode,
    } = this.state

    const collapsed = { ...collapser.plain }
    const categories = Array.from(Constants.workTypeCategory.values())
    // let arrToDownload: any[] = []
    // for (let item of store.contentTasksList) {
    //   if (item.files && item.files.length > 0) {
    //     arrToDownload.push({ contentGuid: item.guid })
    //   }
    // }

    const canDownloadFiles = store.contentTasksList.length > 0 ? store.contentTasksList[0].canDownloadStructure : false
    const canGenerateSheet = store.contentTasksList.length > 0 ? store.contentTasksList[0].canGenerateSheet : false

    const selectedUnit = selectedBlockGuid
      ? order.objects
        .flatMap((o) => o.content)
        .find((u) => u.guid === selectedBlockGuid)
      : null
    const selectedUnitSnapshot = selectedUnit
      ? JSON.parse(JSON.stringify(selectedUnit)) // Простой способ
      : null
    // Находим соответствующий блок в store (если нужно)
    const selectedBlock = selectedBlockGuid
      ? store.contentTasksList.find((item) => item.guid === selectedBlockGuid)
      : null



    const resultWorkers: SelectItem[] = [
      ...employee.employee
        .filter((emp) => !emp.fired)
        .map((e) => ({
          id: e.id,
          label: `(сотр) ${e.label}`,
          color: getEcpColor(e.signDateTo)
        })),
      ...agents.outsourcers.map((e) => ({
        id: e.id,
        label: `(аутс) ${e.label}`,
        color: getEcpColor(e.signDateTo)
      }))
    ]

    let newItems: any[] = []
    order.objects.forEach((object) => {
      object.content.forEach((unit) => {
        newItems.push(unit)
      })
    })

    return (
      <div className={styles.content}>
        {/* Диалог предупреждения об отсутствии или просрочке ЭЦП */}
        <Dialog
          title="Внимание! Проблемы с ЭЦП"
          isOpen={this.state.showNoEcpWarning}
          onClose={() => this.setState({ showNoEcpWarning: false })}
          backdropClassName="standard"
          className={`${styles.smallDialog} figma-dialog`}
        >
          <div className={`${Classes.DIALOG_BODY}`}>
            <div className={styles.warningContent}>
              <GeneralIcon
                type="general-warning"
                style={{
                  color: "#fbb360",
                  fontSize: "48px",
                  marginBottom: "16px",
                  display: "block",
                  margin: "0 auto 16px"
                }}
              />

              {this.state.noEcpEmployees.length > 0 && (
                <>
                  <p className={styles.warningText}>
                    У следующих участников отсутствует отметка о наличии ЭЦП в карточке:
                  </p>
                  <div className={styles.noEcpList}>
                    {this.state.noEcpEmployees.map((emp, index) => (
                      <div key={`noecp-${index}`} className={styles.noEcpItem}>
                        • {emp.name} ({emp.type}) - {emp.personType}
                      </div>
                    ))}
                  </div>
                </>
              )}

              {this.state.expiredEcpEmployees.length > 0 && (
                <>
                  <p className={styles.warningText}>
                    У следующих участников истек срок действия ЭЦП:
                  </p>
                  <div className={styles.noEcpList}>
                    {this.state.expiredEcpEmployees.map((emp, index) => (
                      <div key={`expired-${index}`} className={styles.expiredEcpItem}>
                        • {emp.name} ({emp.type}) - {emp.personType}, истекла {emp.expiryDate}
                      </div>
                    ))}
                  </div>
                </>
              )}

              <p className={styles.warningText}>
                Продолжить сохранение?
              </p>
            </div>
          </div>
          <div className={Classes.DIALOG_FOOTER}>
            <Buttons
              left={
                <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end", width: "100%" }}>
                  <PlanrButton
                    size="small"
                    type="greenish"
                    onClick={() => {
                      this.setState({ showNoEcpWarning: false })
                      this.performSaveDevelopers()
                    }}
                  >
                    Сохранить
                  </PlanrButton>
                  <PlanrButton
                    size="small"
                    type="graybtn"
                    onClick={() => this.setState({ showNoEcpWarning: false })}
                  >
                    Отмена
                  </PlanrButton>
                </div>
              }
            />
          </div>
        </Dialog>
        {/* Диалог подписей */}
        <Dialog
          title={`Виды работ (электронные подписи)`}
          isOpen={showSheet}
          onClose={this.toggleShowDevelopers}
          backdropClassName="standard"
          className={`${styles.ResultWorkersDialog} figma-dialog`}
        >
          <div className={`${Classes.DIALOG_BODY} `}>
            <div className={styles.mainWindow}>
              <div className={styles.column}>
                <div className={styles.columnRow} >
                  <div className={styles.label} style={{ fontSize: "12px" }}>
                    Сотрудник/Аутсорс
                  </div>
                  <div className={styles.label} style={{ fontSize: "12px" }}>
                    Вид работы
                  </div>
                  <div className={styles.icon}> </div>
                </div>
                {employersWorked.map((emplWorked, index) => (
                  <div className={styles.columnRow} key={index}>
                    <div className={styles.label}>
                      {emplWorked.name}
                    </div>
                    <div className={styles.label}>
                      {this.showType(emplWorked.type)}
                    </div>
                    <div className={styles.icon}>
                      <GeneralIcon
                        type="general-trash"
                        style={{
                          color: "#E31818",
                          cursor: "pointer",
                        }}
                        onClick={() => {
                          this.removeDeveloper(index, 'worked')
                        }}
                      />
                    </div>
                  </div>
                ))}

                <div className={styles.selectRow}>
                  <div className={styles.label}>
                    <SimpleSelect
                      key={implementerSelectKey}
                      className={`full-width-select ${Classes.FILL}`}
                      activeItem={implementer}
                      items={resultWorkers}
                      itemRenderer={renderSingleOption}
                      // disabled={selectedType.id === 'gip'}
                      onItemSelect={(i) => {
                        this.setState({ implementer: i })
                      }}
                      popoverProps={{
                        rootBoundary: "viewport",
                        popoverClassName: "import-spendings-table__select",
                      }}
                      inputProps={{
                        placeholder: "Участник",
                      }}
                      filterable={true}
                      itemPredicate={filterItemPredicate}
                    >
                      <DefaultSelectedOption option={implementer} style={{ overflow: "hidden", maxWidth: "340px" }} />
                    </SimpleSelect>
                  </div>
                  <div className={styles.label}>
                    <SimpleSelect
                      className={`full-width-select ${Classes.FILL}`}
                      activeItem={selectedType}
                      items={signatureTypeList}
                      itemRenderer={renderSingleOption}
                      onItemSelect={(i) => {
                        this.setState({ selectedType: i })
                        if (i.id === 'gip') {
                          const gip = resultWorkers.find((emp) => emp.id === this.props.order.employer?.id)
                          if (gip) {
                            this.setState({ implementer: gip })
                          }
                        }
                      }}
                      popoverProps={{
                        rootBoundary: "viewport",
                        popoverClassName: "import-spendings-table__select",
                      }}
                      inputProps={{
                        placeholder: "Вид работы",
                      }}
                      filterable={false}
                    >
                      <DefaultSelectedOption option={selectedType} style={{ overflow: "hidden", maxWidth: "200px" }} />
                    </SimpleSelect>
                  </div>
                  <div className={styles.icon}>
                    <PlanrButton
                      type="secondary"
                      size="small"
                      onClick={() => this.addNewDeveloper()}
                      style={{ marginLeft: "-120px" }}
                    >
                      Добавить
                    </PlanrButton>
                  </div>
                </div>
              </div>
            </div>
            <div className={styles.window}>
              <div className={styles.columnWindow} style={{ width: "30%" }}>
                <div className={styles.dialogLabel}>Юр. лицо (необязательно)</div>
                <GeneralIcon
                  size={20}
                  type={legal ? "general-checkbox" : "general-zero-checkbox"}
                  style={{
                    color: "#1dd278",
                    cursor: "pointer",
                    marginLeft: "10px",
                    marginTop: "15px"
                  }}
                  onClick={() => {
                    if (!order.projectPortfolioId) {
                      portfolios.notify.error("Не выбран Исполнитель на вкладке Основные данные!")
                      return
                    }

                    const selectedPortfolio = portfolios.portfolios.find((p) => p.id === order.projectPortfolioId)
                    if (!selectedPortfolio) {
                      portfolios.notify.error(
                        "Выбранный Исполнитель на вкладке Основные данные не найден в разделе Управление организацией → Организации!"
                      )
                      return
                    }
                    if (!selectedPortfolio.director) {
                      portfolios.notify.error(
                        `Необходимо указать директора для ${selectedPortfolio.label} в разделе Управление организацией → Организации!`
                      )
                      return
                    }
                    if (selectedPortfolio && selectedPortfolio.director) {
                      const director = employee.employee.find((emp) => emp.id === selectedPortfolio.director?.id)
                      if (director) {
                        this.setState({ legal: !this.state.legal })
                        this.addNewLegal(director)
                      }
                    }
                  }}
                />
              </div>
            </div>
            {legal && (
              <div className={styles.window}>
                <div className={styles.column} style={{ width: "30%" }}>
                  {employersLegal.map((emplWorked, index) => (
                    <div className={styles.columnRow} key={index}>
                      <div className={styles.label}>
                        {emplWorked.name}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className={Classes.DIALOG_FOOTER}>
            <Buttons
              left={
                <div style={{ display: "flex" }}>
                  <PlanrButton size="small" type="greenish" onClick={this.saveDevelopers}>
                    Сохранить
                  </PlanrButton>
                  <PlanrButton size="small" type="graybtn" onClick={this.toggleShowDevelopers}>
                    Отмена
                  </PlanrButton>
                </div>
              }
            />
          </div>
        </Dialog>

        {/* Диалог загрузки файлов */}
        <Dialog
          title={`Загрузка файлов результата выполнения работ`}
          isOpen={showUploadFiles}
          onClose={this.toggleShowUploadFiles}
          backdropClassName="standard"
          className={`${styles.FilesDialog} figma-dialog`}
        >
          <div className={`${Classes.DIALOG_BODY} `}>
            <div className={styles.instructionBox}>
              <GeneralIcon type="general-info" style={{ marginRight: '8px', flexShrink: 0 }} />
              <span>
                Вы можете загрузить любое количество исходных файлов и файлов результата. Если загружаемый файл с таким именем уже существует, его версия автоматически увеличится
              </span>
            </div>
            <div className={styles.addFilesDialog}>
              <div className={styles.addFilesDialogColumn}>
                <div className={styles.dialogLabel}>Исходные файлы</div>
                {workFiles.map((file, index) => {
                  const sourceFiles = store.contentTasksList
                    .find((item) => item.guid === workGuid)
                    ?.sourceFiles || []

                  const existingFiles = sourceFiles.filter((f) => f.name === file.name)
                  const isNewVersion = existingFiles.length > 0
                  const nextVersion = isNewVersion
                    ? Math.max(...existingFiles.map((f) => f.version)) + 1
                    : 1

                  return (
                    <div key={index} className={styles.fileItemWithBadge}>
                      <FileLink<any>
                        baseUrl={baseUrl}
                        file={{ fileId: file.id, fileName: file.name }}
                        style={{
                          height: "50px",
                          width: "100%",
                          border: "3px dashed #edf0f2",
                          marginBottom: "15px",
                        }}
                        readOnly={false}
                        onRemove={() => {
                          this.removeWorkFilesFromState(file.id)
                        }}
                      />
                      <span className={isNewVersion ? styles.versionBadge : styles.newFileBadge}>
                        {isNewVersion ? `Новая версия_${nextVersion}` : "Новый файл"}
                      </span>
                    </div>
                  )
                })}
                <PlanRDndUploader
                  accept={"*"}
                  multiple={true}
                  withLoader={true}
                  style={{
                    border: "3px dashed #edf0f2",
                    height: "50px",
                    width: "100%",
                  }}
                  onFileSelected={this.workFilesUpload}
                />
              </div>
              <div className={styles.addFilesDialogColumn}>
                <div className={styles.dialogLabel}>Файлы результата</div>
                {taskFiless.map((file, index) => {
                  const resultFiles = store.contentTasksList
                    .find((item) => item.guid === workGuid)
                    ?.resultFiles || []

                  const existingFiles = resultFiles.filter((f) => f.name === file.name)
                  const isNewVersion = existingFiles.length > 0
                  const nextVersion = isNewVersion
                    ? Math.max(...existingFiles.map((f) => f.version)) + 1
                    : 1

                  return (
                    <div key={index} className={styles.fileItemWithBadge}>
                      <FileLink<any>
                        key={index}
                        baseUrl={baseUrl}
                        file={{ fileId: file.id, fileName: file.name }}
                        style={{
                          height: "50px",
                          width: "100%",
                          border: "3px dashed #edf0f2",
                          marginBottom: "15px",
                        }}
                        readOnly={false}
                        onRemove={() => {
                          this.removeTaskFilesFromState(file.id)
                        }}
                      />
                      <span className={isNewVersion ? styles.versionBadge : styles.newFileBadge}>
                        {isNewVersion ? `Новая версия ${nextVersion}` : "Новый файл"}
                      </span>
                    </div>
                  )
                })}
                <PlanRDndUploader
                  accept={"*"}
                  multiple={true}
                  withLoader={true}
                  style={{
                    border: "3px dashed #edf0f2",
                    height: "50px",
                    width: "100%",
                  }}
                  onFileSelected={this.taskFilesUpload}
                />
              </div>
            </div>
          </div>
          <div className={Classes.DIALOG_FOOTER}>
            <Buttons
              left={
                <div style={{ display: "flex" }}>
                  <PlanrButton
                    size="small"
                    type="greenish"
                    onClick={this.saveUploadedWorkFiles}
                    style={{ minWidth: "120px" }}
                    disabled={isUploading}
                  >
                    {isUploading && <Spinner intent="primary" size={16} />}
                    {!isUploading && "Сохранить"}
                  </PlanrButton>
                  <PlanrButton size="small" type="graybtn" onClick={this.toggleShowUploadFiles}>
                    Отмена
                  </PlanrButton>
                </div>
              }
            />
          </div>
        </Dialog>

        {/* Диалог удаления задания */}
        <Dialog
          title={`Удаление задания`}
          isOpen={showDelete}
          onClose={this.toggleDeleteShow}
          backdropClassName="standard"
          className={`${styles.smallDialog} figma-dialog`}
        >
          <div className={`${Classes.DIALOG_BODY} `}>
            <div>Вы действительно хотите удалить задание?</div>
          </div>
          <div className={Classes.DIALOG_FOOTER}>
            <Buttons
              left={
                <div style={{ display: "flex" }}>
                  <PlanrButton
                    size="small"
                    type="danger"
                    onClick={() => {
                      this.deleteTask(taskId)
                      this.toggleDeleteShow()
                    }}
                  >
                    Удалить
                  </PlanrButton>
                  <PlanrButton size="small" type="graybtn" onClick={this.toggleDeleteShow}>
                    Отмена
                  </PlanrButton>
                </div>
              }
            />
          </div>
        </Dialog>

        {/* Диалог задания */}
        <Dialog
          title={`Задание`}
          isOpen={showAdd}
          onClose={this.closeAddDialog}
          backdropClassName="standard"
          className={`${styles.smallDialog} figma-dialog`}
          style={{ width: "700px" }}
        >
          <div className={`${Classes.DIALOG_BODY} `}>
            <div>
              <div className={styles.dialogLabel}>Название</div>
              <div className={`${Classes.INPUT_GROUP}  planr-default-input`}>
                <TextArea
                  growVertically={true}
                  rows={1}
                  autoComplete="off"
                  placeholder=""
                  data-lpignore="true"
                  value={description}
                  className={`${Classes.FILL} planr-default-input`}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                    this.setState({ description: e.target.value })
                  }}
                />
              </div>
            </div>
            <div style={{ display: "flex", width: "100%" }}>
              <div style={{ width: "50%" }}>
                <div className={styles.dialogLabel}>Дата окончания</div>
                <DatePicker
                  clasName="planr-default-input"
                  value={stopDate}
                  onChange={(date) => {
                    this.setState({ stopDate: date })
                  }}
                />
              </div>
              <div style={{ width: "50%", marginLeft: "20px" }}>
                <div className={styles.dialogLabel}>Исполнитель</div>
                <SimpleSelect
                  className={`full-width-select ${Classes.FILL}`}
                  activeItem={implementer}
                  items={employee.employee.filter((emp) => !emp.fired)}
                  itemRenderer={renderSingleOption}
                  onItemSelect={(i) => {
                    this.setState({ implementer: i })
                  }}
                  popoverProps={{
                    rootBoundary: "viewport",
                    popoverClassName: "import-spendings-table__select",
                  }}
                  inputProps={{
                    placeholder: "Расход",
                  }}
                  filterable={true}
                  itemPredicate={filterItemPredicate}
                >
                  <DefaultSelectedOption option={implementer} />
                </SimpleSelect>
              </div>
            </div>
            <div className={styles.dialogLabel}>Файлы задания</div>

            {taskFiles && taskFiles.length > 0 && taskFiles.map((file) => (
              <div style={{ display: "flex", width: "100%" }} key={file.id}>
                <div style={{ width: "50%" }}>
                  <div className={styles.dialogLabelSmall}>Путь к файлу</div>
                  <div className={`${Classes.INPUT_GROUP}  planr-default-input`} style={{ width: "100%" }}>
                    <InputGroup
                      className="planr-default-input"
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        let newFiles: TTaskFilePath[] = []
                        taskFiles.forEach((f) => {
                          let newItem: TTaskFilePath = {
                            id: f.id,
                            path: f.path,
                            name: f.name,
                          }
                          if (f.id === file.id) {
                            newItem.path = e.target.value
                          }
                          newFiles.push(newItem)
                        })
                        this.setState({ taskFiles: newFiles })
                      }}
                      value={file.path}
                      style={{ fontSize: "12px" }}
                      title={file.path}
                    />
                  </div>
                </div>
                <div style={{ width: "50%", marginLeft: "10px" }}>
                  <div className={styles.dialogLabelSmall}>Файл задания</div>
                  <FileLink<any>
                    key={file.id}
                    baseUrl={baseUrl}
                    file={{ fileId: file.id, fileName: file.name }}
                    style={{
                      height: "38px",
                      width: "100%",
                      border: "3px dashed #edf0f2",
                    }}
                    readOnly={false}
                    onRemove={() => {
                      this.resetFiles(file.id)
                    }}
                  />
                </div>
              </div>
            ))}

            <PlanRDndUploader
              accept={"*"}
              multiple={true}
              withLoader={true}
              style={{
                border: "3px dashed #edf0f2",
                height: "50px",
                width: "100%",
                maxWidth: "100%",
                marginTop: "5px",
              }}
              onFileSelected={this.taskFilesLoad}
            />
            {!isEditeMode && (
              <div>
                <div className={styles.dialogLabel}>Список работ</div>
                <div className={styles.workItemContainer}>
                  {newItems.map((unit) => {
                    const item = saveUnits.find((u) => u === unit.guid)
                    return item ? (
                      <div className={styles.workItem} style={{ fontSize: "13px" }} key={unit.guid}>
                        <PlanrButton
                          icon={item ? "general-checkbox" : "general-zero-checkbox"}
                          type="neutral"
                          size="small"
                          onClick={() => {
                            this.setSaveUnits(unit.guid)
                          }}
                        />
                        <div>{` ${unit.name} ${unit.description}`}</div>
                      </div>
                    ) : null
                  })}
                </div>
                <PlanrButton
                  size="small"
                  type="secondary"
                  onClick={this.showSelectedUnits}
                  className={styles.changeWorkListBtn}
                >
                  Изменить
                </PlanrButton>
              </div>
            )}
          </div>
          <div className={Classes.DIALOG_FOOTER}>
            <Buttons
              left={
                <div style={{ display: "flex" }}>
                  <PlanrButton
                    size="small"
                    type="greenish"
                    onClick={() => {
                      this.saveTask()
                    }}
                  >
                    Сохранить
                  </PlanrButton>
                  <PlanrButton size="small" type="graybtn" onClick={this.closeAddDialog}>
                    Отмена
                  </PlanrButton>
                </div>
              }
            />
          </div>
        </Dialog>

        {/* Диалог выбора видов работ */}
        <Dialog
          title={`Виды работ`}
          isOpen={showUnits}
          onClose={this.cancelSelectedUnits}
          backdropClassName="standard"
          className={`${styles.smallDialog} ${styles.workDialog} figma-dialog`}
        >
          <div className={`${Classes.DIALOG_BODY} `}>
            <div className={styles.workItemContainer}>
              {newItems.map((unit) => {
                const item = selectedUnits.find((u) => u === unit.guid)
                return (
                  <div className={styles.workItem} key={unit.guid}>
                    <PlanrButton
                      icon={item ? "general-checkbox" : "general-zero-checkbox"}
                      type="neutral"
                      size="small"
                      onClick={() => {
                        this.setSelectedUnits(unit.guid)
                      }}
                    />
                    <div>{` ${unit.name} ${unit.description}`}</div>
                  </div>
                )
              })}
            </div>
          </div>
          <div className={Classes.DIALOG_FOOTER}>
            <Buttons
              left={
                <div style={{ display: "flex" }}>
                  <PlanrButton size="small" type="greenish" onClick={this.saveSelectedUnits}>
                    Сохранить
                  </PlanrButton>
                  <PlanrButton size="small" type="graybtn" onClick={this.cancelSelectedUnits}>
                    Отмена
                  </PlanrButton>
                </div>
              }
            />
          </div>
        </Dialog>

        {/* Диалог подтверждения сброса подписей */}
        <Dialog
          title="Внимание!"
          isOpen={showResetWarning}
          onClose={() => this.setState({ showResetWarning: false })}
          backdropClassName="standard"
          className={`${styles.smallDialog} figma-dialog`}
        >
          <div className={`${Classes.DIALOG_BODY}`}>
            <div className={styles.warningContent}>
              <GeneralIcon
                type="general-warning"
                style={{
                  color: "#fbb360",
                  fontSize: "48px",
                  marginBottom: "16px",
                  display: "block",
                  margin: "0 auto 16px"
                }}
              />
              <p className={styles.warningText}>
                В списке отсутствует человек, который уже подписывал файл.
              </p>
              <p className={styles.warningText}>
                Все существующие подписи будут сброшены.
              </p>
              <p className={styles.warningText}>
                Вы действительно хотите продолжить?
              </p>
            </div>
          </div>
          <div className={Classes.DIALOG_FOOTER}>
            <Buttons
              left={
                <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end", width: "100%" }}>
                  <PlanrButton
                    size="small"
                    type="danger"
                    onClick={this.confirmSaveDevelopers}
                  >
                    Продолжить
                  </PlanrButton>
                  <PlanrButton
                    size="small"
                    type="graybtn"
                    onClick={() => this.setState({ showResetWarning: false })}
                  >
                    Отмена
                  </PlanrButton>
                </div>
              }
            />
          </div>
        </Dialog>


        {/* Диалог выбора категорий для скачивания */}
        <Dialog
          title="Выбор разделов для скачивания"
          isOpen={this.state.showDownloadDialog}
          onClose={() => this.setState({ showDownloadDialog: false })}
          backdropClassName="standard"
          className={`${styles.downloadDialog} figma-dialog`}
        >
          <div className={`${Classes.DIALOG_BODY}`}>
            <div className={styles.downloadDialogContent}>
              {/* Фиксированная верхняя панель */}
              <div className={styles.fixedHeader}>
                <div className={styles.headerRow}>
                  {/* Переключатель для исходных файлов */}
                  <div
                    className={styles.optionItem}
                    onClick={this.toggleDownloadSourceFiles}
                  >
                    <GeneralIcon
                      type={this.state.downloadSourceFiles ? "general-checkbox" : "general-zero-checkbox"}
                      style={{ color: "#1dd278", marginRight: "8px" }}
                    />
                    <span>Включить в архив исходные файлы</span>
                  </div>

                  {/* Кнопки выбора */}
                  <div className={styles.selectionButtons}>
                    <PlanrButton
                      type="neutral"
                      size="small"
                      onClick={this.selectAllCategories}
                    >
                      Выбрать все
                    </PlanrButton>
                    <PlanrButton
                      type="neutral"
                      size="small"
                      onClick={this.deselectAllCategories}
                    >
                      Снять все
                    </PlanrButton>
                  </div>

                  {/* Счетчик выбранных */}
                  <span className={styles.selectedCount}>
                    Выбрано: {this.state.selectedCategoriesForDownload.length} из {this.state.availableCategories.length}
                  </span>
                </div>
                {/* <div className={styles.optionNote}>
                  Файлы результатов скачиваются всегда
                </div> */}
              </div>

              {/* Прокручиваемый список категорий */}
              <div className={styles.scrollableCategories}>
                {/* Группируем по категориям */}
                {Array.from(new Set(this.state.availableCategories.map((c) => c.category))).map((category) => {
                  const itemsInCategory = this.state.availableCategories.filter((c) => c.category === category)
                  const allSelected = this.state.selectAllInCategory[category]

                  return (
                    <div key={category} className={styles.categoryGroup}>
                      <div className={styles.categoryGroupHeader}>
                        <div
                          className={styles.categoryGroupTitle}
                          onClick={() => this.toggleAllInCategory(category)}
                        >
                          <GeneralIcon
                            type={allSelected ? "general-checkbox" : "general-zero-checkbox"}
                            style={{ color: "#1dd278", marginRight: "8px" }}
                          />
                          <span className={styles.categoryName}>{category}</span>
                          <span className={styles.categoryCount}>
                            ({itemsInCategory.length} разделов, {itemsInCategory.reduce((sum, item) => sum + item.totalFilesCount, 0)} файлов)
                          </span>
                        </div>
                      </div>

                      <div className={styles.categoryItems}>
                        {itemsInCategory.map((item) => {
                          const isSelected = this.state.selectedCategoriesForDownload.includes(item.guid)

                          return (
                            <div
                              key={item.guid}
                              className={`${styles.categoryItem} ${isSelected ? styles.selected : ''}`}
                              onClick={() => this.toggleCategorySelection(item.guid)}
                            >
                              <GeneralIcon
                                type={isSelected ? "general-checkbox" : "general-zero-checkbox"}
                                style={{ color: "#1dd278", marginRight: "8px", flexShrink: 0 }}
                              />
                              <div className={styles.itemInfo}>
                                <div className={styles.itemTitleRow}>
                                  <span className={styles.itemName}>{item.name}</span>
                                  <div className={styles.itemFilesDetails}>
                                    {item.sourceFilesCount > 0 && (
                                      <span className={styles.sourceFilesBadge}>
                                        исх: {item.sourceFilesCount}
                                      </span>
                                    )}
                                    {item.resultFilesCount > 0 && (
                                      <span className={styles.resultFilesBadge}>
                                        рез: {item.resultFilesCount}
                                      </span>
                                    )}
                                    <span className={styles.totalFilesCount}>
                                      {item.totalFilesCount}
                                    </span>
                                  </div>
                                </div>
                                {item.description && (
                                  <span className={styles.itemDescription}>{item.description}</span>
                                )}
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
          <div className={Classes.DIALOG_FOOTER}>
            <Buttons
              left={
                <div style={{ display: "flex", gap: "8px" }}>
                  <PlanrButton
                    size="small"
                    type="greenish"
                    onClick={this.downloadSelectedCategories}
                    disabled={this.state.selectedCategoriesForDownload.length === 0}
                  >
                    Скачать ({this.state.selectedCategoriesForDownload.length})
                  </PlanrButton>
                  <PlanrButton
                    size="small"
                    type="graybtn"
                    onClick={() => this.setState({ showDownloadDialog: false })}
                  >
                    Отмена
                  </PlanrButton>
                </div>
              }
            />
          </div>
        </Dialog>

        {/* Верхняя панель инструментов */}
        <div className="planr-tools" style={{ height: "52px" }}>
          {!readOnly && (
            <div style={{ marginRight: "8px" }}>
              <StandardFormInput
                name={clientCanDownloadField}
                schema={schema}
                className="planr-form-input"
                inline={true}
              >
                {({ field, form }) => (
                  <PlanrButton
                    type={field.value === true ? "blueish" : "graybtn"}
                    icon={"general-check"}
                    onClick={() => {
                      form.setFieldValue(field.name, !field.value)
                      form.setFieldTouched(field.name, true)
                    }}
                    round
                    title="Доступ к скачиванию для заказчика"
                  />
                )}
              </StandardFormInput>
            </div>
          )}
          {!readOnly && canGenerateSheet && (
            <PlanrButton
              type="graybtn"
              icon="general-export"
              onClick={() => {
                store.downloadSheets(order.id)
              }}
              round={true}
              title="Сформировать ИУЛ для всех разделов"
            />
          )}

          {/* {canDownloadFiles && (
            <DownloadButton
              gray={true}
              loading={this.state.downloading}
              onClick={async () => {
                try {
                  this.setState({ downloading: true })
                  await store.downloadFiles(order.id, arrToDownload, this.props.order.name)
                } catch {
                  return
                } finally {
                  if (this.mounted) {
                    this.setState({ downloading: false })
                  }
                }
              }}
            />
          )} */}
          {canDownloadFiles && (
            <DownloadButton
              round
              loading={this.state.downloading}
              onClick={this.openDownloadDialog}
            />
          )}
          <CollapseExpandAll onChange={this.collapseAll} />
        </div>

        {/* Основной контент с двумя колонками */}
        <div className={styles.splitView}>
          {/* Левая колонка - категории и блоки */}
          <div className={styles.categoriesColumn}>
            <Field name={name}>
              {(fieldProps: FieldProps) => {
                const objects = sortOrderObjects(order.objects)
                this.fieldProps = fieldProps
                return (
                  <div className={styles.contents} ref={this.me}>
                    {categories.map((category) => {
                      return (
                        <CategoryBlock
                          employer={order.employer}
                          baseUrl={baseUrl}
                          key={category}
                          category={category}
                          collapsed={!collapsed[category]}
                          onToggleCollapse={() => collapser.toggle(category)}
                          fieldProps={fieldProps}
                          collapserContent={collapserContent}
                          objects={objects}
                          readOnly={readOnly}
                          store={store}
                          orderId={order.id}
                          orderName={order.name}
                          orderPortfolioId={order.projectPortfolioId}
                          user={user}
                          userId={this.props.userId}
                          highlightRow={this.recordPointer}
                          employee={employee}
                          agents={agents}
                          portfolios={portfolios}
                          selectedBlockGuid={selectedBlockGuid}
                          onSelectBlock={(guid) => this.setState({ selectedBlockGuid: guid })}
                        />
                      )
                    })}
                  </div>
                )
              }}
            </Field>
          </div>

          {/* Правая колонка - содержимое выбранного блока */}
          <div className={styles.blockContentColumn}>
            {selectedBlockGuid && selectedBlock && selectedUnitSnapshot ? (
              <RowTask
                outsourcers={agents.outsourcers}
                contentTask={selectedBlock}
                work={selectedUnitSnapshot}
                baseUrl={baseUrl}
                user={this.props.userId}
                showDeleteTask={this.showDeleteTask}
                showAddTask={this.showAddTask}
                returnTask={this.returnTask}
                addAccept={this.addAccept}
                addComplete={this.addComplete}
                addConfirm={this.addConfirm}
                addCancell={this.addCancell}
                showUploadFiles={this.showUploadFilesDialog}
                showDevelopers={this.showDevelopersDialog}
                showEditTask={(
                  descr: string,
                  stopDate: string | null,
                  taskId: string,
                  file: any,
                  guid: string,
                  employerId: string | null
                ) => {
                  // Приводим file к нужному типу TTaskFilePath[]
                  const taskFiles = Array.isArray(file) ? file : []
                  this.showEditTask(descr, stopDate, taskId, taskFiles, guid, employerId)
                }}
                downloadFiles={this.downloadFiles}
                downloadVersion={this.downloadVersion}
                deleteVersion={this.deleteVersion}
                collapsed={false}
                onToggleCollapse={() => { }}
                readOnly={readOnly}
                highlighted={this.recordPointer}
                onSubmitSignature={this.handleSubmitSignature}
                onAttachSignature={this.handleAttachSignature}
                onClearSignatures={this.handleClearSignatures}
                onNotifyError={this.handleNotifyError}
                onNotifySuccess={this.handleNotifySuccess}
                onGetSignature={this.handleGetSignature}
                orderId={order.id}
                orderName={`${order.fullInventoryNumber} ${order.name}`}
                gipId={order.employer?.id}
                saveFilePath={this.saveFilePath}
                people={resultWorkers}
              />
            ) : (
              <div className={styles.noBlockSelected}>
                Выберите раздел для просмотра содержимого
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }
}

export const ContentTask = withRouter(observer(ContentBase))

interface ContentProps {
  name: string
  readOnly: boolean
  collapser: SectionCollapserType
  collapserContent: SectionCollapserType
  order: OrderSummaryType
  store: OrderContentTasksDictionaryType
  clientCanDownloadField: string
  baseUrl: string
  user: string
  userId: string
  schema: Schema<any>
  employee: EmployerDictionaryType
  agents: OutsourcerDictionaryType
  portfolios: ProjectPortfolioDictionaryType
}